import { Volume } from '@/api/type-server'
import { Button, Divider, Trans } from '@/components'
import { useDomain } from '@/contexts/DomainContext'
import { Machine } from '@/models/machine'

const NoDriveRegionMap: Record<number, string> = {
  3: 'JP-01',
}

type MountDriveSectionProps = {
  machine: Machine | undefined
  mountVolume: Volume | undefined
  mountPath: string
  readWriteMode: boolean
  cloudVolumes: Volume[]
  setMountModalOpen: (open: boolean) => void
  resetMountConfig: () => void
  navigate: (path: string) => void
}

export function MountDriveSection({
  machine,
  mountVolume,
  mountPath,
  readWriteMode,
  cloudVolumes,
  setMountModalOpen,
  resetMountConfig,
  navigate,
}: MountDriveSectionProps) {
  const { domainInfos } = useDomain()

  return (
    <div className="mt-8 px-8">
      <div className="t-sb mb-2 text-secondary">
        <Trans i18nKey="create.mount">Mount Data Hub</Trans>
      </div>

      {mountVolume ? (
        <div className="bg-panel-gray-100 rounded-lg py-4 t-sm text-tertiary ">
          <div className="flex flex-wrap gap-y-4">
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderRegion">Region</Trans>
              </div>
              <div className="text-primary">
                {domainInfos.find((d) => d.domain === mountVolume.domain)?.displayName}
              </div>
            </div>
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderUsage">Usage</Trans>
              </div>
              <div className="text-primary t-sb flex">
                {Math.ceil(mountVolume.usage_mb / 1024 || 0)}
                <div className="text-tertiary">
                  /{Math.ceil(mountVolume.quota_gb)}
                  GB
                </div>
              </div>
            </div>
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderMountPath">Mount Path</Trans>
              </div>
              <div className="text-primary">{mountPath}</div>
            </div>
            <div className="w-1/4 px-4">
              <div className="text-secondary">
                <Trans i18nKey="create.volumeTableHeaderPermissions">Permissions</Trans>
              </div>
              <div className="text-primary">{readWriteMode ? 'Read only' : 'Read & write'}</div>
            </div>
          </div>

          {/* action buttons */}
          <div className="px-4">
            <Divider className="my-4" />
            <div className="flex gap-4">
              <div
                className="text-btn-secondary cursor-pointer"
                onClick={() => setMountModalOpen(true)}
              >
                <Trans i18nKey="create.volumeTableHeaderEdit">Edit</Trans>
              </div>
              <div
                className="text-red-500 cursor-pointer"
                onClick={() => {
                  resetMountConfig()
                }}
              >
                <Trans i18nKey="create.volumeTableHeaderUnmount">Unmount</Trans>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // not mounted
        <>
          {(!machine || (machine && cloudVolumes.length > 0)) && (
            <Button type="outline" onClick={() => setMountModalOpen(true)} disabled={!machine}>
              <Trans i18nKey="create.mountAsANetworkDrive">Mount</Trans>
            </Button>
          )}

          {!machine && (
            <div className="t-sm text-warning mt-2">
              <Trans i18nKey="create.selectMachineWarning">* Please select the machine first</Trans>
            </div>
          )}

          {machine && cloudVolumes.length === 0 && (
            <>
              {machine.domains.some((d) => NoDriveRegionMap[d]) ? (
                /* no datadrive in this region  */
                <>
                  <Button type="outline" disabled>
                    <Trans i18nKey="create.mountAsANetworkDrive">Mount</Trans>
                  </Button>
                  <div className="t-sm text-warning mt-2">
                    <Trans
                      i18nKey="create.mountAsNetworkDriveNotExist"
                      values={{
                        region: domainInfos.find((d) => d.domain === machine.domains[0])
                          ?.displayName,
                      }}
                    >
                      * The Data Hub feature in region JP-01 is currently being upgraded. Stay
                      tuned!
                    </Trans>
                  </div>
                </>
              ) : (
                /* no datadrive in this region, redirect to Storage Capacity */
                <>
                  <Button
                    type="outline"
                    onClick={() => {
                      navigate('/space')
                    }}
                  >
                    <Trans i18nKey="create.spaceManagement">Storage Capacity</Trans>
                  </Button>
                  <div className="t-sm text-warning mt-2">
                    <Trans
                      i18nKey="create.mountAsNetworkDriveNotAvailable"
                      values={{
                        region: domainInfos.find((d) => d.domain === machine.domains[0])
                          ?.displayName,
                      }}
                    >
                      * There are currently no available Data Hubs in this region. Please manage
                      your storage capacity.
                    </Trans>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
